/**
 * Shared room-ordering rules, used by both the "Add Multiple Rooms" modal and the
 * client questionnaire so a room list reads the same way however it was created.
 */

/**
 * Work out which sleeping/bathing pair a room name belongs to, so Bathroom 3 can
 * be kept next to Bedroom 3 instead of drifting to the bottom of the list.
 * Returns { key, kind } or null for rooms that aren't part of a pair.
 *   'Bedroom 2'       -> { key: '|2',       kind: 'bed'  }
 *   'Bathroom 2'      -> { key: '|2',       kind: 'bath' }
 *   'Guest Room 3'    -> { key: 'guest|3',  kind: 'bed'  }
 *   'Primary Bathroom'-> { key: 'primary|', kind: 'bath' }
 *   'Living Room'     -> null
 */
export const pairKeyFor = (name) => {
  const s = String(name || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const m = s.match(/^(.*?)\s*\b(bedroom|bathroom|bath|room)\b\s*(\d+)?$/);
  if (!m) return null;
  const [, prefix, word, num] = m;

  let kind;
  if (word === 'bathroom' || word === 'bath') kind = 'bath';
  else if (word === 'bedroom') kind = 'bed';
  else {
    // Bare "Room" is only a bedroom in "Guest Room N" - never Living/Dining/Powder Room.
    if (prefix !== 'guest') return null;
    kind = 'bed';
  }
  return { key: `${prefix}|${num || ''}`, kind };
};

/**
 * Stable reorder that pulls each bathroom up to sit directly after its matching
 * bedroom. Anything unpaired keeps its original position.
 */
export const pairRooms = (names) => {
  const bedKeys = new Set(
    names.map(pairKeyFor).filter(k => k && k.kind === 'bed').map(k => k.key)
  );
  const out = [];
  const used = new Set();

  names.forEach(name => {
    if (used.has(name)) return;
    const info = pairKeyFor(name);
    // Hold a bathroom back if its bedroom is in the list - it goes out with the pair.
    if (info && info.kind === 'bath' && bedKeys.has(info.key)) return;

    out.push(name);
    used.add(name);

    if (info && info.kind === 'bed') {
      const mate = names.find(other => {
        if (used.has(other)) return false;
        const oi = pairKeyFor(other);
        return oi && oi.kind === 'bath' && oi.key === info.key;
      });
      if (mate) {
        out.push(mate);
        used.add(mate);
      }
    }
  });

  return out;
};

/**
 * "Entire Home" is a scope answer, not a room - a project must never end up with a
 * single room literally called "Entire Home". When a client picks it we ask which
 * rooms they actually have, pre-ticking this set so they only untick what's missing.
 */
export const ENTIRE_HOME = 'Entire Home';

export const WHOLE_HOME_DEFAULT_ROOMS = [
  'Foyer',
  'Living Room',
  'Kitchen',
  'Dining Room',
  'Primary Bedroom',
  'Primary Bathroom',
  'Guest Bedroom 1',
  'Guest Bathroom 1',
  'Half Bathroom',
  'Laundry Room',
];

/** Drop the "Entire Home" scope marker and put the real rooms in pair order. */
export const roomsToCreate = (selected = []) => {
  const real = [...new Set(selected)].filter(
    r => String(r).trim().toLowerCase() !== ENTIRE_HOME.toLowerCase()
  );
  return pairRooms(real);
};
